/**
 * What the checkout return screen is allowed to tell a patient (ADR-0045).
 *
 * The screen used to headline "Payment received" for every visitor — including
 * one who abandoned Paystack or whose card was declined (M20 finding F5). It
 * could not do better, because ADR-0021 keeps the patient session in memory and
 * the Paystack round-trip destroys it, so the screen had no credential with
 * which to ask anything.
 *
 * This endpoint is keyed on the REFERENCE instead, which Paystack hands back in
 * the redirect URL. It is a read: nothing about the booking moves, and the
 * webhook remains the only thing that secures it (ADR-0020).
 */
import { apiFetch } from '../lib/api'

/**
 * `pending` means WE DO NOT KNOW — not that nothing happened. Paystack usually
 * redirects before its webhook lands, so it is the normal first answer to a
 * perfectly good payment, and the screen must not read it as bad news.
 *
 * `failed` and `abandoned` are kept apart because they need different
 * sentences: one person's card was declined, the other never finished paying.
 */
export type PaymentStatus = 'succeeded' | 'pending' | 'failed' | 'abandoned'

export const paymentStatusKey = (reference: string) =>
  ['payment-status', reference] as const

/** GET /api/v1/payments/{reference}/status. Throws `ApiError` (404 unknown). */
export async function fetchPaymentStatus(
  reference: string,
  signal?: AbortSignal,
): Promise<PaymentStatus> {
  const body = await apiFetch<{ data: { status: PaymentStatus } }>(
    `/api/v1/payments/${encodeURIComponent(reference)}/status`,
    signal ? { signal } : {},
  )
  return body.data.status
}
